/**
 * SOLID Review
 * S — Strong. One concern: answer the table's questions about a score from the server's ranking — which claims
 *     beat a floor, what a score is called, which dice it stands for, whether it is Meyer or a pair. It ranks
 *     nothing itself.
 * O — Good. A new question about a combination is one more query over the same ranks.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. Five small queries and the factory; each caller uses the ones it needs.
 * D — Strong. The rank's schema type only.
 */
import type { IEntityClientDiceRank } from '../ports/schemas/DiceRankingMoves';

/**
 * THE SERVER'S DICE RANKING, AS A VALUE OBJECT (frontend-domain-oo P2, 2026-10-26). It replaces
 * `DiceRankingService.ts` — `VALID_SCORES`, `MEYER_RANK`, `scoreLabel` and `scoreToDice`, the client's own copy of
 * the backend's `DicePair` — and the `isDouble` range in `game/RevealTransforms.ts`. Every answer is looked up in
 * the ranking the server served (`diceRanking`, the backend's `DicePair.catalogue()`); none is worked out from a
 * score's digits.
 *
 * A SCORE THE RANKING DOES NOT HOLD — every score, before the ranking has arrived; afterwards, a number no
 * combination has — names no combination: its label is the number as it came, it stands for no dice, and it is
 * neither Meyer nor a pair. Nothing beats a floor until the ranking is here.
 */
export class DiceRanking {
  private constructor(
    /** Lowest first. */
    private readonly ranks: ReadonlyArray<IEntityClientDiceRank>,
  ) {}

  /** The ranking as served, kept in the server's order: by each combination's place, whatever order it came in. */
  static of(ranks: ReadonlyArray<IEntityClientDiceRank>): DiceRanking {
    return new DiceRanking([...ranks].sort((a, b) => a.rank - b.rank));
  }

  /**
   * Every combination that beats `floor`, lowest first. A score IS the place in the server's order, so a
   * combination beats the floor when its score is higher — the backend's `DicePair.beats`; matching is not enough.
   */
  beating(floor: number): IEntityClientDiceRank[] {
    return this.ranks.filter((rank) => rank.score > floor);
  }

  /** How the screen names a score: the server's label — or, for a score it does not rank, the number itself. */
  labelOf(score: number): string {
    return this.find(score)?.label ?? String(score);
  }

  /** The two dice a score stands for, high first; none for a score the ranking does not hold. */
  diceOf(score: number): [number, number] | null {
    const rank = this.find(score);
    return rank === undefined ? null : [rank.die1, rank.die2];
  }

  /** Meyer, as the server marks it. */
  isMeyer(score: number): boolean {
    return this.find(score)?.isMeyer === true;
  }

  /** A pair, as the server marks it — Little Meyer is not one. */
  isDouble(score: number): boolean {
    return this.find(score)?.isDouble === true;
  }

  private find(score: number): IEntityClientDiceRank | undefined {
    return this.ranks.find((rank) => rank.score === score);
  }
}
